import type { Node } from 'react'
import React, { Component } from 'react'
import { CCAlert } from 'app/ecommerce/common/components/modal-alert'
import { AlertModal } from 'app/common/modals/alert'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import type { RestaurantShortDTO } from 'shared/types'
import { featureCodes, restaurantServices } from 'shared/utils'
import { getService } from 'shared/utils/ecommerce/utils'
import { isEcommerceModeActive } from 'shared/utils/ecommerce'
import { ChooseOrderTypeModal } from '../../common/components/choose-order-type'
import { isMobile, isTablet } from 'react-device-detect'

const { CLICK_AND_COLLECT_ON_SITE, SERVICE_TABLE } = restaurantServices

type Props = {
    hasCart: boolean,
    disabled?: boolean,
    restaurant?: RestaurantShortDTO,
    pickUpType?: string,
    onClick?: () => void,
    onDeliverySet?: () => void,
    setRestaurant: (restaurant: RestaurantShortDTO) => void,
    isRestaurantAvailable: (restaurantId: string) => boolean,
    setCollectMode: (collectMode: string) => void,
    fetchRestaurantData: (frNumber: string) => RestaurantShortDTO,
    fetchCatalog: (id: string) => void,
    navigate: (to: string, state?: any) => void,
    children: Node,
    style: Object,
    intl: IntlShape,
    availableFeatures?: string[]
}

type State = {
    ccAlert: boolean,
    openModeModal: boolean,
    modalOpened: boolean,
    error: ?string
}

class CcCartCheck extends Component<Props, State> {
    constructor(props) {
        super(props)
        this.state = {
            ccAlert: false,
            error: null,
            openModeModal: false,
            modalOpened: false
        }
    }

    checkDelivery = (
        restaurant: RestaurantShortDTO,
        pickUpType?: string
    ): boolean => {
        const { navigate, intl } = this.props
        const { services } = restaurant
        if (pickUpType) {
            const collectMode = getService(pickUpType)
            if (
                !services ||
                !services.includes(collectMode) ||
                (collectMode === SERVICE_TABLE &&
                    !services.includes(CLICK_AND_COLLECT_ON_SITE))
            ) {
                navigate(
                    intl.formatMessage({
                        id: 'routes.clickandcollect.delivery.choice'
                    })
                )
                return false
            }
            return true
        }
        return false
    }

    checkRestaurant = async (
        restaurantId?: string
    ): Promise<?RestaurantShortDTO> => {
        const { isRestaurantAvailable, fetchRestaurantData } = this.props
        const updatedRestaurant =
            restaurantId && (await fetchRestaurantData(restaurantId))
        if (!updatedRestaurant) {
            return undefined
        }
        const { id, services } = updatedRestaurant
        if (!services || !isEcommerceModeActive(updatedRestaurant)) {
            this.setState({
                error: 'clickandcollect.service.closed',
                ccAlert: false
            })
            return undefined
        } else {
            const available = await isRestaurantAvailable(id)
            if (!available) {
                this.setState({
                    error: 'clickandcollect.restaurant.quota',
                    ccAlert: false
                })
                return undefined
            }
        }
        return updatedRestaurant
    }

    handleClick = async () => {
        const {
            onClick,
            restaurant,
            hasCart,
            pickUpType,
            navigate,
            setRestaurant,
            setCollectMode,
            intl,
            availableFeatures
        } = this.props

        const newOrderFlowEnabled =
            !!availableFeatures &&
            availableFeatures.includes(featureCodes.ENABLE_NEW_ORDER_FLOW)

        onClick && onClick()
        if (hasCart && !newOrderFlowEnabled) {
            this.setState({ ccAlert: true })
            return
        }
        if (restaurant) {
            const updatedRestaurant = await this.checkRestaurant(restaurant.id)
            if (!!updatedRestaurant) {
                if (
                    restaurant.services.includes(
                        restaurantServices.TABLE_ORDER
                    ) &&
                    (isMobile || isTablet) &&
                    !this.state.modalOpened
                ) {
                    this.setState({ openModeModal: true, modalOpened: true })
                } else {
                    this.setState({ modalOpened: false })
                    await setRestaurant(updatedRestaurant)
                    if (pickUpType) {
                        const deliveryAvailable = this.checkDelivery(
                            updatedRestaurant,
                            pickUpType
                        )
                        deliveryAvailable && (await setCollectMode(pickUpType))
                        await this.fetchCatalogAndOpenModal()
                    } else {
                        if (newOrderFlowEnabled) {
                            document.location.href =
                                intl.formatMessage({
                                    id: 'routes.clickandcollect.delivery.choice'
                                }) + `/${updatedRestaurant.id}`
                        } else {
                            navigate(
                                intl.formatMessage({
                                    id: 'routes.clickandcollect.delivery.choice'
                                })
                            )
                        }
                    }
                }
            }
        } else {
            isMobile || isTablet
                ? this.setState({ openModeModal: true })
                : newOrderFlowEnabled
                ? (document.location.href = intl.formatMessage({
                      id: 'routes.clickandcollect.restaurant.choice'
                  }))
                : navigate(
                      intl.formatMessage({
                          id: 'routes.clickandcollect.restaurant.choice'
                      })
                  )
        }
    }

    fetchCatalogAndOpenModal = async () => {
        const { restaurant, onDeliverySet, fetchCatalog } = this.props
        const { id } = restaurant || {}
        try {
            await fetchCatalog(id)
            onDeliverySet && onDeliverySet()
        } catch (error) {
            this.setState({ error })
        }
    }

    render() {
        const {
            children,
            disabled,
            restaurant,
            pickUpType,
            onDeliverySet,
            intl,
            style,
            setRestaurant,
            setCollectMode,
            navigate
        } = this.props
        const { ccAlert, error } = this.state

        const deliveryChoiceRoute = intl.formatMessage({
            id: 'routes.clickandcollect.delivery.choice'
        })
        const restaurantChoiceRoute = intl.formatMessage({
            id: 'routes.clickandcollect.restaurant.choice'
        })
        return (
            <div style={style}>
                {ccAlert && (
                    <CCAlert
                        closeAllAlerts={() => this.setState({ ccAlert: false })}
                        onDeliverySet={onDeliverySet}
                        goTo={
                            restaurant
                                ? deliveryChoiceRoute
                                : restaurantChoiceRoute
                        }
                        restaurant={restaurant}
                        pickUpType={pickUpType}
                        checkRestaurant={this.checkRestaurant}
                        checkDelivery={this.checkDelivery}
                    />
                )}
                {!!error && (
                    <AlertModal
                        isOpen={!!error}
                        alertTitleKey="oops"
                        alertContentKey={error}
                        okLabel="close"
                        handleClose={() => this.setState({ error: null })}
                        onOkClick={() => this.setState({ error: null })}
                    />
                )}
                <div
                    onClick={e => {
                        e.stopPropagation()
                        !disabled && this.handleClick()
                    }}>
                    {children}
                </div>
                <ChooseOrderTypeModal
                    visible={this.state.openModeModal}
                    close={() => {
                        this.setState({
                            openModeModal: false,
                            modalOpened: false
                        })
                    }}
                    restaurant={restaurant}
                    handleCcClick={this.handleClick}
                />
            </div>
        )
    }
}

const CcCartCheckComponent = injectIntl(CcCartCheck)
export { CcCartCheckComponent }
